/**
 * Productivity Insights Component
 *
 * AI-powered analysis of work patterns, completions, and recommendations.
 */

import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  TrendingUp,
  BarChart3,
  Loader2,
  AlertCircle,
  Check,
  Sparkles,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { getProductivityInsights, getCachedInsights } from '../api/client';
import type { ProductivityInsight, InsightsResponse } from '../types';

interface ProductivityInsightsProps {
  onBack: () => void;
}

export function ProductivityInsights({ onBack }: ProductivityInsightsProps) {
  const [isGettingInsights, setIsGettingInsights] = useState(false);
  const [isLoadingCached, setIsLoadingCached] = useState(true);
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    getCachedInsights(7)
      .then((cached) => {
        if (cached) {
          setInsights(cached);
          setGeneratedAt(cached.generatedAt);
        }
      })
      .catch(() => { /* cached load failure is silent */ })
      .finally(() => setIsLoadingCached(false));
  }, []);

  const friendlyError = (err: any): string => {
    const msg: string = err.message || '';
    if (msg.includes('API key not configured') || msg.includes('unavailable')) {
      return 'AI features require a Claude API key. Set CLAUDE_API_KEY in your server environment.';
    }
    return msg;
  };

  const handleGetInsights = async () => {
    setIsGettingInsights(true);
    setError(null);
    setSuccess(null);
    setInsights(null);

    try {
      const result = await getProductivityInsights(7, instructions || undefined);
      setInsights(result);
      setGeneratedAt(new Date().toISOString());
      setSuccess('Insights generated successfully');
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setIsGettingInsights(false);
    }
  };

  const getInsightIcon = (type: ProductivityInsight['type']) => {
    switch (type) {
      case 'pattern': return TrendingUp;
      case 'recommendation': return Sparkles;
      case 'achievement': return Check;
      default: return BarChart3;
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">Productivity Insights</h1>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Discover patterns in your work habits</p>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
            <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
          </div>
        )}

        {/* Main card */}
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleGetInsights}
              disabled={isGettingInsights || isLoadingCached}
              className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGettingInsights ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing Productivity...
                </>
              ) : isLoadingCached ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : insights ? (
                <>
                  <TrendingUp className="w-4 h-4" />
                  Refresh Insights
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4" />
                  Get Insights
                </>
              )}
            </button>
          </div>
          {generatedAt && !isGettingInsights && (
            <p className="text-xs text-text-tertiary-light dark:text-text-secondary-dark">
              Last generated {new Date(generatedAt).toLocaleString()}
            </p>
          )}

          {/* Custom instructions toggle */}
          <button
            onClick={() => setShowInstructions((v) => !v)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors border border-transparent hover:border-border-subtle-light dark:hover:border-border-subtle-dark"
          >
            {showInstructions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Custom instructions
            {instructions && <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />}
          </button>
          {showInstructions && (
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Focus on work tasks only, ignore home maintenance"
              rows={3}
              className="w-full px-3 py-2 bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-sm text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          )}

          {/* Insights Display */}
          {insights && (
            <div className="space-y-4 pt-2">
              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-surface-base-light dark:bg-surface-hover-dark rounded-lg">
                  <div className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">{insights.stats.tasksCompleted}</div>
                  <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Completed</div>
                </div>
                {insights.stats.averageCompletionTime && (
                  <div className="text-center p-3 bg-surface-base-light dark:bg-surface-hover-dark rounded-lg">
                    <div className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                      {Math.round(insights.stats.averageCompletionTime)}m
                    </div>
                    <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Avg Time</div>
                  </div>
                )}
                {insights.stats.mostProductiveCategory && (
                  <div className="text-center p-3 bg-surface-base-light dark:bg-surface-hover-dark rounded-lg">
                    <div className="text-sm font-bold text-text-primary-light dark:text-text-primary-dark truncate">
                      {insights.stats.mostProductiveCategory}
                    </div>
                    <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Top Category</div>
                  </div>
                )}
              </div>

              {/* Insights List */}
              {insights.insights.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Insights:</h3>
                  {insights.insights.map((insight, index) => {
                    const Icon = getInsightIcon(insight.type);
                    return (
                      <div key={index} className="flex items-start gap-3 p-3 bg-surface-base-light dark:bg-surface-hover-dark rounded-lg">
                        <Icon className="w-4 h-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">{insight.title}</p>
                          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{insight.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recommendations */}
              {insights.recommendations.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Recommendations:</h3>
                  <ul className="space-y-1">
                    {insights.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm text-text-secondary-light dark:text-text-secondary-dark flex items-start gap-2">
                        <span className="text-orange-500">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
